<template>
  <d-modal
    size="lg"
    @close="handleClose">
    <d-modal-header>
      <d-modal-title>
        {{ $t('shipments.popups.shipmentInfo') }}
      </d-modal-title>
    </d-modal-header>
    <d-modal-body>
      <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>

      <div v-if="allDataLoaded && !loadError">
        <h4 class="mt-0">{{ $t('shipments.popups.basicDataTitle') }}</h4>

        <div class="form-group">
          <table class="table table-sm">
            <tr>
              <td style="width: 50%;">
                {{ $t('shipments.popups.relatedOrder') }}:
              </td>
              <td>
                <router-link
                  v-if="order_id"
                  :to="'/zamowienia/edycja/' + parseInt(order_id, 10)">
                  <template v-if="order_name">{{ order_name }}</template>
                  <template v-else>{{ $t('ui.lack') }}</template>
                </router-link>
                <small v-else>
                  {{ $t('shipments.noRelatedOrder') }}
                </small>
              </td>
            </tr>
            <tr>
              <td style="width: 50%;">
                {{ $t('shipments.popups.trackingNumber') }}:
              </td>
              <td>
                <small v-if="tracking_number">
                  {{ tracking_number }}
                </small>
                <small v-else>
                  {{ $t('ui.lack') }}
                </small>
              </td>
            </tr>
            <tr>
              <td style="width: 50%;">
                {{ $t('shipments.status') }}:
              </td>
              <td>
                <d-badge
                  :theme="statusBadgeTheme[status]">
                  {{ $t('shipments.statuses.' + status.toLowerCase()) }}
                </d-badge>

                <div
                  v-if="error_code"
                  class="text-danger">
                  <small>
                    <strong>{{ error_code }}</strong>:
                    {{ error_message }}
                  </small>
                </div>
              </td>
            </tr>
          </table>
        </div>

        <h4 class="mt-5">{{ $t('shipments.popups.receiverDataTitle') }}</h4>

        <div class="form-group">
          <table class="table table-sm">
            <tr><td style="width: 50%;">{{ $t('shipments.popups.receiverName') }}:</td><td>{{ receiver_name }}</td></tr>
            <tr><td style="width: 50%;">{{ $t('shipments.popups.receiverPhone') }}:</td><td>{{ receiver_phone }}</td></tr>
            <tr><td style="width: 50%;">{{ $t('shipments.popups.receiverEmail') }}:</td><td>{{ receiver_email }}</td></tr>
            <tr><td style="width: 50%;">{{ $t('shipments.popups.receiverCountryCode') }}:</td><td>{{ receiver_address_country_code }}</td></tr>
            <tr><td style="width: 50%;">{{ $t('shipments.popups.receiverPostalCode') }}:</td><td>{{ receiver_address_postal_code }}</td></tr>
            <tr><td style="width: 50%;">{{ $t('shipments.popups.receiverCity') }}:</td><td>{{ receiver_address_city }}</td></tr>
            <tr>
              <td style="width: 50%;">
                {{ $t('shipments.popups.receiverAddress') }}:
              </td>
              <td>
                {{ receiver_street }} {{ receiver_building }} <template v-if="receiver_locale">/ {{ receiver_locale }}</template>
                <template v-if="receiver_address_line_2">
                  <br>
                  {{ receiver_address_line_2 }}
                </template>
                <template v-if="receiver_address_line_3">
                  <br>
                  {{ receiver_address_line_3 }}
                </template>
              </td>
            </tr>
          </table>
        </div>

        <h4 class="mt-5">{{ $t('shipments.popups.shipperDataTitle') }}</h4>

        <div class="form-group">
          <table class="table table-sm">
            <tr><td style="width: 50%;">{{ $t('shipments.popups.shipperNumber') }}:</td><td>{{ shipper_number }}</td></tr>
            <tr><td style="width: 50%;">{{ $t('shipments.popups.shipperName') }}:</td><td>{{ shipper_name }}</td></tr>
            <tr><td style="width: 50%;">{{ $t('shipments.popups.shipperPhone') }}:</td><td>{{ shipper_phone }}</td></tr>
            <tr><td style="width: 50%;">{{ $t('shipments.popups.shipperEmail') }}:</td><td>{{ shipper_email }}</td></tr>
            <tr><td style="width: 50%;">{{ $t('shipments.popups.shipperCountryCode') }}:</td><td>{{ shipper_address_country_code }}</td></tr>
            <tr><td style="width: 50%;">{{ $t('shipments.popups.shipperPostalCode') }}:</td><td>{{ shipper_address_postal_code }}</td></tr>
            <tr><td style="width: 50%;">{{ $t('shipments.popups.shipperCity') }}:</td><td>{{ shipper_address_city }}</td></tr>
            <tr>
              <td style="width: 50%;">
                {{ $t('shipments.popups.shipperAddress') }}:
              </td>
              <td>
                {{ shipper_street }} {{ shipper_building }} <template v-if="shipper_locale"> / {{ shipper_locale }}</template>
              </td>
            </tr>
          </table>
        </div>

        <h4 class="mt-5">{{ $t('shipments.popups.serviceDataTitle') }}</h4>

        <div class="form-group">
          <label>{{ $t('shipments.popups.upsService') }}:</label><br>
          {{ service_name }}
        </div>
        <div
          v-if="reference_number"
          class="form-group">
          <label>{{ $t('shipments.popups.packageReferenceNumber') }}:</label><br>
          {{ reference_number }}
        </div>
        <div
          v-if="reference_number_2"
          class="form-group">
          <label>{{ $t('shipments.popups.packageReferenceNumber') }} 2:</label><br>
          {{ reference_number_2 }}
        </div>
        <div
          v-if="description"
          class="form-group">
          <label>{{ $t('shipments.popups.shipmentDescription') }}:</label><br>
          {{ description }}
        </div>

        <h4 class="d-flex mt-5">{{ $t('shipments.popups.packagesDataTitle') }}</h4>

        <div class="form-group">
          <table class="table mt-2 mb-0 full-width">
            <thead class="bg-light">
              <tr>
                <th scope="col" class="border-0" style="width: 120px;">{{ $t('shipments.popups.packageWeight') }}</th>
                <th scope="col" class="border-0" style="width: 100px;">{{ $t('shipments.trackingNumber') }}</th>
                <th scope="col" class="border-0" style="width: calc(50% - 110px)">{{ $t('shipments.popups.packageReferenceNumber') }}</th>
                <th scope="col" class="border-0" style="width: calc(50% - 110px)">{{ $t('shipments.popups.packageDescription') }}</th>
              </tr>
            </thead>
            <tbody v-if="packages.length">
              <tr
                v-for="(packageItem, index) of packages"
                :key="'package-' + index">
                <td class="align-middle">
                  {{ packageItem.weight }} kg
                </td>
                <td class="align-middle">
                  <small>{{ packageItem.tracking_number }}</small>
                </td>
                <td class="align-middle">
                  <small>{{ packageItem.reference_number }}</small><br>
                  <small>{{ packageItem.reference_number_2 }}</small>
                </td>
                <td class="align-middle">
                  <small>{{ packageItem.description }}</small>
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="4" class="text-center">
                  {{ $t('shipments.popups.noPackages') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <h4 class="mt-5">{{ $t('shipments.popups.paymentDataTitle') }}</h4>

        <div class="form-group">
          <table class="table table-sm">
            <tr><td style="width: 50%;">{{ $t('shipments.popups.useCOD') }}</td><td><span v-if="is_cod" class="text-success">{{ $t('ui.yes') }}</span><span v-else class="text-danger">{{ $t('ui.no') }}</span></td></tr>
            <tr v-if="is_cod"><td style="width: 50%;">{{ $t('shipments.popups.codCurrencyCode') }}:</td><td>{{ cod_currency_code }}</td></tr>
            <tr v-if="is_cod"><td style="width: 50%;">{{ $t('shipments.popups.codAmount') }}:</td><td>{{ cod_amount }}</td></tr>
          </table>
        </div>

        <h4 class="mt-5">{{ $t('shipments.popups.shipmentTrackingStatus') }}</h4>

        <div class="form-group">
          <table class="table mt-2 mb-0 full-width">
            <thead class="bg-light">
              <tr>
                <th scope="col" class="border-0" style="width: 150px;">{{ $t('shipments.status') }}</th>
                <th scope="col" class="border-0">{{ $t('shipments.statusDate') }}</th>
              </tr>
            </thead>
            <tbody v-if="trackings.length">
              <tr
                v-for="(trackingStatusItem, index) of trackings"
                :key="'tracking-status-' + index">
                <td class="align-middle">
                  <d-badge
                    v-if="trackingStatusItem.code"
                    :theme="trackingStatusBadgeTheme[trackingStatusItem.code]">
                    {{ $t('shipments.trackingStatuses.' + trackingStatusItem.code) }}
                  </d-badge>
                </td>
                <td class="align-middle">
                  {{ trackingStatusItem.status_at }}
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="2" class="text-center">
                  {{ $t('shipments.popups.noTrackings') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </d-modal-body>
  </d-modal>
</template>

<script>
import FormUtils from '../../utils/FormUtils.js';
import CustomerPanel from '../../mixins/CustomerPanel';

export default {
  name: 'shipment-view-popup',
  props: [
    'shipmentID'
  ],
  mixins: [
    CustomerPanel
  ],
  computed: {
    allDataLoaded () {
      return this.loaded;
    },
    statusBadgeTheme () {
      return {
        NEW: 'primary',
        ERROR: 'danger',
        INTEGRATED: 'info',
        CANCELLED: 'dark',
        DELIVERED: 'success'
      };
    },
    trackingStatusBadgeTheme () {
      return {
        I: 'warning',
        D: 'success',
        X: 'danger',
        P: 'info',
        M: 'dark'
      };
    }
  },
  data () {
    return {
      shipper_number: '',
      shipper_name: '',
      shipper_phone: '',
      shipper_email: '',
      shipper_address_country_code: '',
      shipper_address_postal_code: '',
      shipper_address_city: '',
      shipper_street: '',
      shipper_building: '',
      shipper_locale: '',
      receiver_name: '',
      receiver_phone: '',
      receiver_email: '',
      receiver_address_country_code: null,
      receiver_address_postal_code: '',
      receiver_address_city: '',
      receiver_street: '',
      receiver_building: '',
      receiver_locale: '',
      receiver_address_line_2: '',
      receiver_address_line_3: '',
      tracking_number: '',
      status: '',
      reference_number: '',
      reference_number_2: '',
      service_name: '',
      is_cod: false,
      cod_amount: '',
      cod_currency_code: null,
      description: '',
      order_id: null,
      packages: [],
      trackings: [],
      error_code: '',
      error_message: '',
      // UI flags
      loadError: false,
      loaded: false
    };
  },
  mounted () {
    FormUtils.loadItemData(this, {
      endpointBase: this.getApiEndpoint('/shipments/item/'),
      overrideID: parseInt(this.shipmentID, 10),
      instanceFields: ['shipper_number', 'shipper_name', 'shipper_phone', 'shipper_email', 'shipper_address_country_code', 'shipper_address_postal_code', 'shipper_address_city', 'shipper_street', 'shipper_building', 'shipper_locale', 'receiver_name', 'receiver_phone', 'receiver_email', 'receiver_address_country_code', 'receiver_address_postal_code', 'receiver_address_city', 'receiver_address_line_2', 'receiver_address_line_3', 'receiver_street', 'receiver_building', 'receiver_locale', 'tracking_number', 'status', 'service_name', 'is_cod', 'cod_amount', 'cod_currency_code', 'description', 'packages', 'order_id', 'order_name', 'reference_number', 'reference_number_2', 'trackings', 'error_code', 'error_message'],
      loadedKey: 'loaded',
      errorKey: 'loadError'
    });
  },
  methods: {
    handleClose () {
      this.$bus.$emit('view-shipment-popup-close');
    },
    cancel () {
      this.handleClose();
    }
  }
}
</script>
